<template>
	<form class="position-relative" @change="formStateChanged">
		<div
			v-if="isSpinnerVisible('autofill')"
			class="loader d-flex flex-column justify-content-center align-items-center text-center mx-auto form-group col-md-12 col-lg-10 col-xxl-8"
		>
			<ProgressSpinner animation-duration="1.75s" stroke-width="4" />
		</div>

		<div class="form-row">
			<div class="form-group col-md-12 col-lg-10 col-xxl-8">
				<InputWithValidation
					v-slot="{ field }"
					:is-required="validations['companyName'].required"
					:label="$t('common.forms.lblCompanyName')"
					name="companyName"
				>
					<InputText
						v-model="field.value.value"
						:class="{ 'p-invalid': field.errors?.length }"
						:name="field.name"
						@blur="field.setTouched(true)"
					/>
				</InputWithValidation>
			</div>
		</div>
		<div class="form-row">
			<div class="form-group col-md-12 col-lg-10 col-xxl-8">
				<InputWithValidation
					v-slot="{ field }"
					:is-required="validations['street'].required"
					:label="$t('common.forms.lblAddress')"
					name="street"
				>
					<InputText
						v-model="field.value.value"
						:class="{ 'p-invalid': field.errors?.length }"
						:name="field.name"
						@blur="field.setTouched(true)"
					/>
				</InputWithValidation>
			</div>
		</div>
		<div class="form-row">
			<div class="form-group col-md-6 col-lg-5 col-xxl-4">
				<InputWithValidation
					v-slot="{ field }"
					:is-required="validations['postalCode'].required"
					:label="$t('common.forms.lblPostalCode')"
					name="postalCode"
				>
					<InputText
						v-model="field.value.value"
						:class="{ 'p-invalid': field.errors?.length }"
						:name="field.name"
						@blur="field.setTouched(true)"
					/>
				</InputWithValidation>
			</div>
			<div class="form-group col-md-6 col-lg-5 col-xxl-4">
				<InputWithValidation
					v-slot="{ field }"
					:is-required="validations['city'].required"
					:label="$t('common.forms.lblCity')"
					name="city"
				>
					<InputText
						v-model="field.value.value"
						:class="{ 'p-invalid': field.errors?.length }"
						:name="field.name"
						@blur="field.setTouched(true)"
					/>
				</InputWithValidation>
			</div>
		</div>
		<div class="form-row">
			<div class="form-group col-md-12 col-lg-10 col-xxl-8">
				<InputWithValidation
					v-slot="{ field }"
					:is-required="validations['countryCode'].required"
					:label="$t('common.forms.lblCountry')"
					name="countryCode"
				>
					<Dropdown
						:class="{ 'p-invalid': field.errors?.length }"
						:disabled="countries?.length === 1"
						:model-value="field.value.value"
						option-label="value"
						option-value="key"
						:options="countries"
						@change="field.setTouched(true)"
						@update:modelValue="
							(e) => {
								field.value.value = e;
								field.setTouched(true);
								formStateChanged();
							}
						"
					/>
				</InputWithValidation>
			</div>
		</div>
		<div class="form-row">
			<div v-if="useIban" class="form-group col-md-12 col-lg-10 col-xxl-8">
				<InputWithValidation
					v-slot="{ field }"
					:info="$t('iban.infoIcon')"
					:is-required="validations['iban'].required"
					:label="$t('common.forms.lblIban')"
					name="iban"
				>
					<div class="p-input-icon-right w-100">
						<InputText
							v-model.lazy.trim="field.value.value"
							:class="{ 'p-invalid': field.errors?.length }"
							:name="field.name"
							@blur="
								formatIban(field.value.value);
								field.setTouched(true);
							"
						/>
						<i v-if="field.meta.pending" class="pi pi-spin pi-spinner" />
					</div>
				</InputWithValidation>
			</div>
		</div>
		<div class="form-row">
			<div class="form-group col-md-6 col-lg-5 col-xxl-4">
				<InputWithValidation
					v-slot="{ field }"
					:is-required="validations['cocNumber'].required"
					:label="$t('common.forms.lblCocNumber')"
					name="cocNumber"
				>
					<InputText
						v-model="field.value.value"
						:class="{ 'p-invalid': field.errors?.length }"
						:name="field.name"
						@blur="field.setTouched(true)"
					/>
				</InputWithValidation>
			</div>
			<div class="form-group col-md-6 col-lg-5 col-xxl-4">
				<InputWithValidation
					v-slot="{ field }"
					:info="$t('vat.infoIcon')"
					:is-required="validations['vatNumber'].required"
					:label="$t('common.forms.lblVatNumber')"
					name="vatNumber"
				>
					<div class="p-input-icon-right w-100">
						<InputText
							v-model.trim.lazy="field.value.value"
							:class="{ 'p-invalid': field.errors?.length }"
							:name="field.name"
							@blur="field.setTouched(true)"
						/>
						<i v-if="field.meta.pending" class="pi pi-spin pi-spinner" />
					</div>
				</InputWithValidation>

				<!--<small v-else-if="v$.companyForm.vatNumber.validVatNumber?.$invalid" class="p-error">
					{{ v$.companyForm.vatNumber.validVatNumber?.$message }}
					<i18n-t keypath="vat.invalidInfo">
						<template #link>
							<a :href="$t('vat.invalidLink')" target="_blank">{{ $t("vat.invalidLinkPlaceholder") }}</a>
						</template>
					</i18n-t>
				</small>-->
			</div>
		</div>
	</form>
</template>

<script lang="ts">
import BaseComponent from "@/components/base/baseComponent.vue";
import InputWithValidation from "@/components/common/inputWithValidation.vue";
import { CompanyForm as CompanyFormModel } from "@/types/models/form/companyForm";
import { Component, Emit, Model, Prop, Watch } from "vue-facing-decorator";
import { FormComponent } from "@/types/models/form/formComponent";
import { FormContext, FormMeta, useForm } from "vee-validate";
import { PropType, computed, reactive, unref } from "vue";
import { ValidationHelper } from "@/types/helpers/validationHelper";
import { cloneDeep } from "lodash";
import { friendlyFormatIBAN } from "ibantools";
import ProgressSpinner from "primevue/progressspinner";

@Component({
	components: { InputWithValidation, ProgressSpinner },
	emits: ["update:modelValue"],
})
export default class CompanyForm extends BaseComponent implements FormComponent<CompanyFormModel> {
	@Model({
		type: Object as PropType<CompanyFormModel>,
		required: true,
		default: {},
	})
	model!: CompanyFormModel;

	@Prop({ type: Boolean, required: false, default: false }) useVat!: boolean;
	@Prop({ type: Boolean, required: false, default: false }) useIban!: boolean;
	@Prop({
		type: Object as PropType<{ [key: string]: string }>,
		required: true,
		default: {},
	})
	countries!: { [key: string]: string }[];

	@Emit()
	formStateChanged(): FormMeta<CompanyFormModel> {
		const { meta, values } = this.form;
		this.model = cloneDeep(values);
		return unref(meta);
	}

	form!: FormContext<CompanyFormModel>;

	created(): void {
		if (!this.model.countryCode && this.countries.length === 1) {
			this.model.countryCode = this.countries[0].key;
		}

		this.form = useForm({ initialValues: this.model, validationSchema: this.validations });
	}

	vatRequired = true;

	get validations(): Record<string, unknown> {
		return reactive({
			companyName: { required: true },
			cocNumber: {},
			vatNumber: {
				required: computed(() => this.vatRequired),
				validVatNumber: [this.model.countryCode, this.useVat],
			},
			iban: {
				required: this.isIbanRequired,
				validIbanNumber: this.useIban,
			},
			street: { required: true },
			postalCode: {
				required: true,
				validPostalCode: "@countryCode",
			},
			city: { required: true },
			countryCode: { required: this.countries?.length > 1 },
			dunsNumber: {},
		});
	}

	get isValid(): boolean {
		return unref(this.form.meta)?.valid;
	}

	get isDirty(): boolean {
		return unref(this.form.meta)?.dirty;
	}

	get isPending(): boolean {
		return unref(this.form.meta)?.pending;
	}

	@Watch("form.values.countryCode")
	isVatRequired() {
		this.vatRequired = this.useVat && ValidationHelper.checkIfVatIsRequired(this.model.countryCode || "");
	}

	get isIbanRequired(): boolean {
		return this.useIban;
	}

	formatIban(value: string): void {
		this.form.setFieldValue("iban", friendlyFormatIBAN(value) || this.model.iban);
	}
}
</script>

<style scoped lang="scss">
.loader {
	position: absolute;
	height: 100%;
	z-index: 10;
}
</style>
